import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, Clapperboard, GalleryHorizontal, ListPlus, Loader2, Send,
} from 'lucide-react';
import { SiFacebook, SiInstagram, SiTiktok, SiYoutube } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PublishStatusBadge } from '@/components/publishing/PublishStatusBadge';
import { ProBadge } from '@/components/ProBadge';
import { useHasFeature } from '@/lib/useHasFeature';
import { SOCIAL_PUBLISHING_FEATURE } from '@/lib/publishing/socialPublishingFeature';
import { QUOTE_CAROUSEL_FEATURE } from '@/lib/quoteCarousel/quoteCarouselFeature';
import { useFreecutStudioAccess } from '@/hooks/use-freecut-studio-access';
import { useSocialPostingPlatforms } from '@/hooks/use-social-posting-platforms';
import { clipStudioPath } from '@/lib/freecut-studio/studioPath';
import type { PublishUiStatus } from '@/lib/publishing/types';

interface Props {
  clipId: string;
  /** Rendered and not expired: gates the post menu (queue/TikTok/IG/FB/YT). */
  canPublish: boolean;
  /** Gates the actions that need no render, "Carrossel de frases" and FreeCut
   *  Studio, independently of `canPublish`: both read the source video
   *  (`canMakeQuoteCarousel`). Defaults to `canPublish` for callers that do
   *  not compute it. */
  canMakeCarousel?: boolean;
  compact?: boolean;
  /** The "Carrossel de frases" entry point navigates away from whatever flow
   *  is hosting this component (e.g. the /calendar scheduling picker), so
   *  callers whose click should stay inside their own flow opt out. Default
   *  true for the ordinary clip-card call sites. */
  showCarouselAction?: boolean;
  onSendToQueue?: (clipId: string) => void;
  onSendToTikTok?: (clipId: string) => void;
  onPublishToYouTube?: (clipId: string) => void;
  onPublishToInstagram?: (clipId: string) => void;
  /** Shown only when the viewer may post to Facebook (`useSocialPostingPlatforms`). */
  onPublishToFacebook?: (clipId: string) => void;
  tiktokPublishStatus?: PublishUiStatus;
  tiktokPublishFailReason?: string | null;
  youtubePublishStatus?: PublishUiStatus;
  youtubePublishFailReason?: string | null;
  instagramPublishStatus?: PublishUiStatus;
  instagramPublishFailReason?: string | null;
  facebookPublishStatus?: PublishUiStatus;
  facebookPublishFailReason?: string | null;
}

const stopCardClick = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

/** Shared, isolated controls and live status regions for every clip-card shape. */
export const ClipPublishingControls: React.FC<Props> = ({
  clipId,
  canPublish,
  canMakeCarousel = canPublish,
  compact = false,
  showCarouselAction = true,
  onSendToQueue,
  onSendToTikTok,
  onPublishToYouTube,
  onPublishToInstagram,
  onPublishToFacebook,
  tiktokPublishStatus,
  tiktokPublishFailReason,
  youtubePublishStatus,
  youtubePublishFailReason,
  instagramPublishStatus,
  instagramPublishFailReason,
  facebookPublishStatus,
  facebookPublishFailReason,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasSocialPublishing = useHasFeature(SOCIAL_PUBLISHING_FEATURE);
  const hasQuoteCarousel = useHasFeature(QUOTE_CAROUSEL_FEATURE);
  // The "Carrossel de frases" entry point below is independent of
  // social_publishing (it is reachable without the posting feature — only
  // publishing a carousel requires it, gated inside QuoteCarouselPage
  // itself) and of the render (`canMakeCarousel`), but requires its own
  // quote_carousel plan/subscription feature.
  const showCarousel = showCarouselAction && canMakeCarousel && hasQuoteCarousel;
  // FreeCut Studio opens the clip as it would export, from the source video, so like the
  // carousel it needs no render; it also navigates away, so flows that opt out of
  // `showCarouselAction` hide it too. Admins always see it.
  const hasStudio = useFreecutStudioAccess();
  const showStudio = showCarouselAction && canMakeCarousel && hasStudio;
  // Facebook posting has its own gate on top of social_publishing: until Meta
  // approves it, only some viewers may post, so the handler counts only for them.
  const canPostToFacebook = useSocialPostingPlatforms().includes('facebook');
  const publishToFacebook = canPostToFacebook ? onPublishToFacebook : undefined;

  const supportsTikTok = !!onSendToTikTok || tiktokPublishStatus !== undefined;
  const supportsYouTube = !!onPublishToYouTube || youtubePublishStatus !== undefined;
  const supportsInstagram = !!onPublishToInstagram || instagramPublishStatus !== undefined;
  const supportsFacebook = !!publishToFacebook || facebookPublishStatus !== undefined;
  const supportsQueue = !!onSendToQueue;
  const iconClassName = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const triggerClassName = compact
    ? 'min-w-0 w-full h-auto min-h-11 gap-1.5 whitespace-normal px-2 py-2 text-xs leading-tight'
    : 'w-full';
  const anyPublishing = tiktokPublishStatus === 'publishing'
    || youtubePublishStatus === 'publishing'
    || instagramPublishStatus === 'publishing'
    || facebookPublishStatus === 'publishing';

  const actions = [
    onSendToQueue ? {
      key: 'queue',
      testId: 'send-to-queue',
      label: t('publishQueues.send.action'),
      icon: <ListPlus className={iconClassName} />,
      disabled: false,
      busy: false,
      onSelect: () => onSendToQueue(clipId),
    } : null,
    onSendToTikTok ? {
      key: 'tiktok',
      testId: 'send-to-tiktok',
      label: t('publishing.platforms.tiktok'),
      icon: tiktokPublishStatus === 'publishing'
        ? <Loader2 className={`${iconClassName} animate-spin`} />
        : <SiTiktok className={iconClassName} />,
      disabled: tiktokPublishStatus === 'publishing',
      busy: tiktokPublishStatus === 'publishing',
      onSelect: () => onSendToTikTok(clipId),
    } : null,
    onPublishToInstagram ? {
      key: 'instagram',
      testId: 'publish-to-instagram',
      label: t('publishing.platforms.instagram'),
      icon: instagramPublishStatus === 'publishing'
        ? <Loader2 className={`${iconClassName} animate-spin`} />
        : <SiInstagram className={iconClassName} />,
      disabled: instagramPublishStatus === 'publishing',
      busy: instagramPublishStatus === 'publishing',
      onSelect: () => onPublishToInstagram(clipId),
    } : null,
    publishToFacebook ? {
      key: 'facebook',
      testId: 'publish-to-facebook',
      label: t('publishing.platforms.facebook'),
      icon: facebookPublishStatus === 'publishing'
        ? <Loader2 className={`${iconClassName} animate-spin`} />
        : <SiFacebook className={iconClassName} />,
      disabled: facebookPublishStatus === 'publishing',
      busy: facebookPublishStatus === 'publishing',
      onSelect: () => publishToFacebook(clipId),
    } : null,
    onPublishToYouTube ? {
      key: 'youtube',
      testId: 'publish-to-youtube',
      label: t('publishing.platforms.youtube'),
      icon: youtubePublishStatus === 'publishing'
        ? <Loader2 className={`${iconClassName} animate-spin`} />
        : <SiYoutube className={iconClassName} />,
      disabled: youtubePublishStatus === 'publishing',
      busy: youtubePublishStatus === 'publishing',
      onSelect: () => onPublishToYouTube(clipId),
    } : null,
  ].filter((action): action is NonNullable<typeof action> => action != null);

  // The post menu (queue/TikTok/Instagram/Facebook/YouTube) still requires
  // social_publishing on top of having a handler; the carousel action below
  // does not.
  const showPostControls = canPublish && hasSocialPublishing
    && (supportsQueue || supportsTikTok || supportsYouTube || supportsInstagram || supportsFacebook);

  // Nothing to show at all (e.g. the /calendar picker opts out of the
  // carousel action and has no social_publishing post controls either).
  if (!showPostControls && !showCarousel && !showStudio) return null;

  return (
    <div
      data-testid="clip-publishing-controls"
      className={`w-full min-w-0 ${compact ? 'space-y-1.5' : 'space-y-2'}`}
    >
      {showPostControls && supportsTikTok && (
        <div
          data-testid="tiktok-publish-live-region"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          <PublishStatusBadge
            platform="tiktok"
            status={tiktokPublishStatus ?? 'none'}
            failReason={tiktokPublishFailReason ?? null}
          />
        </div>
      )}
      {showPostControls && supportsInstagram && (
        <div
          data-testid="instagram-publish-live-region"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          <PublishStatusBadge
            platform="instagram"
            status={instagramPublishStatus ?? 'none'}
            failReason={instagramPublishFailReason ?? null}
          />
        </div>
      )}
      {showPostControls && supportsFacebook && (
        <div
          data-testid="facebook-publish-live-region"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          <PublishStatusBadge
            platform="facebook"
            status={facebookPublishStatus ?? 'none'}
            failReason={facebookPublishFailReason ?? null}
          />
        </div>
      )}
      {showPostControls && supportsYouTube && (
        <div
          data-testid="youtube-publish-live-region"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          <PublishStatusBadge
            platform="youtube"
            status={youtubePublishStatus ?? 'none'}
            failReason={youtubePublishFailReason ?? null}
          />
        </div>
      )}
      {showPostControls && actions.length > 0 && (
        <div
          data-testid="clip-publishing-actions"
          className="w-full min-w-0"
          onClick={stopCardClick}
        >
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                data-testid="post-menu-trigger"
                variant="secondary"
                size={compact ? 'sm' : undefined}
                className={triggerClassName}
                aria-busy={anyPublishing}
                onClick={stopCardClick}
              >
                {anyPublishing ? (
                  <Loader2 className={`${iconClassName} animate-spin`} />
                ) : (
                  <Send className={iconClassName} />
                )}
                <span>{t('publishing.post')}</span>
                <ChevronDown className={iconClassName} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-[100] min-w-[11rem]"
              onClick={stopCardClick}
            >
              {actions.map((action) => (
                <DropdownMenuItem
                  key={action.key}
                  data-testid={action.testId}
                  className="cursor-pointer gap-2"
                  disabled={action.disabled}
                  aria-busy={action.busy}
                  onSelect={(event) => {
                    event.preventDefault();
                    action.onSelect();
                  }}
                >
                  {action.icon}
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {showStudio && (
        <div className="w-full min-w-0" onClick={stopCardClick}>
          <Button
            type="button"
            variant="outline"
            data-testid="clip-freecut-studio-action"
            size={compact ? 'sm' : undefined}
            className={triggerClassName}
            onClick={() => navigate(clipStudioPath(clipId))}
          >
            <Clapperboard className={iconClassName} />
            <span>{t('clipStudio.open')}</span>
          </Button>
        </div>
      )}
      {showCarousel && (
        <div className="w-full min-w-0" onClick={stopCardClick}>
          <Button
            type="button"
            variant="outline"
            data-testid="clip-quote-carousel-action"
            size={compact ? 'sm' : undefined}
            className={triggerClassName}
            onClick={() => navigate(`/clips/${clipId}/carousel`)}
          >
            <GalleryHorizontal className={iconClassName} />
            <span>{t('quoteCarousel.title')}</span>
            <ProBadge className={compact ? 'px-1.5 py-0 text-[9px] leading-4' : undefined} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClipPublishingControls;
